import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { BookOpen, Users, Heart, Target, FileText, Calendar, ArrowLeft, ChevronRight } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { AnnouncementsSection, EventsSection, StaffDirectory, ResourcesSection } from "../components/sections";
import { apiCall, API } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

export const AboutPage = () => {
  const values = [
    { icon: BookOpen, title: "Academic Excellence", description: "Rigorous curriculum preparing students for future success" },
    { icon: Users, title: "Inclusive Community", description: "Welcoming environment that celebrates diversity" },
    { icon: Heart, title: "Character Development", description: "Building leaders with integrity and compassion" },
    { icon: Target, title: "Innovation", description: "Embracing new approaches to education and learning" },
  ];

  return (
    <div className="py-16">
      <section className="bg-[#002147] text-white py-20">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold mb-6">About Young Christian Preparatory School/ JHS</h1>
          <p className="text-xl text-gray-300 leading-relaxed">
            Founded in 1970, Young Christian Preparatory School/ JHS has been dedicated to nurturing young minds and
            preparing students for success. Our mission is to provide a transformative educational experience.
          </p>
        </div>
      </section>
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-[#002147] mb-12 text-center">Our Core Values</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <Card key={index} className="card-hover text-center">
                <CardContent className="p-8">
                  <div className="w-16 h-16 bg-[#002147]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <value.icon size={32} className="text-[#002147]" />
                  </div>
                  <h3 className="text-xl font-semibold text-[#002147] mb-2">{value.title}</h3>
                  <p className="text-gray-600">{value.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export const AnnouncementsPage = () => {
  const [announcements, setAnnouncements] = useState([]);
  useEffect(() => {
    const fetchData = async () => {
      try { const data = await apiCall("GET", "/announcements"); setAnnouncements(data); }
      catch (err) { console.error("Failed to fetch announcements:", err); }
    };
    fetchData();
  }, []);
  return <AnnouncementsSection announcements={announcements} />;
};

export const EventsPage = () => {
  const [events, setEvents] = useState([]);
  useEffect(() => {
    const fetchData = async () => {
      try { const data = await apiCall("GET", "/events"); setEvents(data); }
      catch (err) { console.error("Failed to fetch events:", err); }
    };
    fetchData();
  }, []);
  return <EventsSection events={events} />;
};

export const StaffPage = () => <StaffDirectory />;
export const ResourcesPage = () => <ResourcesSection />;
export const AttendancePage = () => <div className="py-16 bg-gradient-to-b from-yellow-400 to-orange-400 min-h-screen"><div className="max-w-7xl mx-auto px-4"><h1 className="text-3xl font-bold text-[#002147]">Attendance</h1><p className="text-[#002147]/70">Coming soon...</p></div></div>;
export const GradesPage = () => <div className="py-16 bg-gradient-to-b from-yellow-400 to-orange-400 min-h-screen"><div className="max-w-7xl mx-auto px-4"><h1 className="text-3xl font-bold text-[#002147]">Grades</h1><p className="text-[#002147]/70">Coming soon...</p></div></div>;
export const TeacherAssignmentsPage = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAssignments = async () => {
      if (!token) { setLoading(false); return; }
      try {
        const res = await fetch(`${API}/assignments`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) setAssignments(await res.json());
      } catch (err) { console.error(err); }
      setLoading(false);
    };
    fetchAssignments();
  }, [token]);

  const myAssignments = assignments.filter(a => a.teacher_id === user?.id);

  const classConfig = [
    { group: "Kindergarten", color: "pink", icon: "GraduationCap", classes: [{ id: "nursery", name: "Nursery" }, { id: "kg1", name: "KG1" }, { id: "kg2", name: "KG2" }] },
    { group: "Primary School", color: "blue", icon: "BookOpen", classes: [{ id: "class-1", name: "Class 1" }, { id: "class-2", name: "Class 2" }, { id: "class-3", name: "Class 3" }, { id: "class-4", name: "Class 4" }, { id: "class-5", name: "Class 5" }, { id: "class-6", name: "Class 6" }] },
    { group: "Junior High School", color: "indigo", icon: "Award", classes: [{ id: "jhs1", name: "JHS1" }, { id: "jhs2", name: "JHS2" }, { id: "jhs3", name: "JHS3" }] }
  ];

  const countByClass = (classId) => myAssignments.filter(a => a.class_id === classId).length;

  return (
    <div className="min-h-screen bg-gradient-to-b from-yellow-400 to-orange-400" data-testid="teacher-assignments-page">
      <div className="bg-gradient-to-r from-[#002147] to-[#003366] text-white py-10">
        <div className="max-w-6xl mx-auto px-4">
          <Button variant="ghost" className="text-white/80 hover:text-white hover:bg-white/10 mb-3" onClick={() => navigate("/teacher-dashboard")}>
            <ArrowLeft size={18} className="mr-2" /> Back to Dashboard
          </Button>
          <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-3">
            <FileText size={28} /> My Assignments
          </h1>
          <p className="text-white/70 mt-1">Manage and post assignments for each class</p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        {/* Class Assignment Links */}
        <Card className="bg-white shadow-lg" data-testid="teacher-class-links">
          <CardHeader className="bg-gradient-to-r from-[#FF6B00] to-orange-500 text-white rounded-t-lg py-4">
            <CardTitle className="text-lg flex items-center gap-2"><Calendar size={20} /> Assignment Bulletin by Class</CardTitle>
            <p className="text-white/80 text-sm mt-1">Click a class to view, post, and manage assignments</p>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {classConfig.map(section => (
                <div key={section.group}>
                  <h3 className="text-sm font-bold text-[#002147] uppercase tracking-wide mb-3">{section.group}</h3>
                  <div className="space-y-2">
                    {section.classes.map(cls => {
                      const count = countByClass(cls.id);
                      const bgColor = section.color === "pink" ? "bg-pink-50 hover:bg-pink-100 border-pink-200" : section.color === "blue" ? "bg-blue-50 hover:bg-blue-100 border-blue-200" : "bg-[#002147]/5 hover:bg-[#002147]/10 border-[#002147]/20";
                      const textColor = section.color === "pink" ? "text-pink-500" : section.color === "blue" ? "text-blue-500" : "text-[#002147]";
                      return (
                        <button key={cls.id} onClick={() => navigate(`/assignments/${cls.id}`)}
                          className={`w-full text-left px-4 py-3 rounded-lg ${bgColor} border text-[#002147] font-medium text-sm transition-all hover:translate-x-1 flex items-center justify-between group`}
                          data-testid={`teacher-class-${cls.id}`}>
                          <span className="flex items-center gap-2">
                            <BookOpen size={14} className={textColor} />
                            {cls.name} Assignments
                          </span>
                          <span className="flex items-center gap-2">
                            {count > 0 && <span className={`text-xs px-2 py-0.5 rounded-full ${section.color === "pink" ? "bg-pink-200 text-pink-700" : section.color === "blue" ? "bg-blue-200 text-blue-700" : "bg-[#002147]/20 text-[#002147]"}`}>{count} posted</span>}
                            <ChevronRight size={14} className="opacity-40 group-hover:translate-x-1 transition-transform" />
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* My Recent Assignments */}
        <Card className="bg-white shadow-lg" data-testid="teacher-recent-assignments">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2 text-[#002147]"><FileText size={20} /> My Recent Assignments ({myAssignments.length})</CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            {loading && <p className="text-sm text-gray-400 text-center py-4">Loading...</p>}
            {!loading && myAssignments.length === 0 && (
              <p className="text-sm text-gray-400 text-center py-8">You haven't posted any assignments yet. Click a class above to get started.</p>
            )}
            {!loading && myAssignments.map(a => (
              <button key={a.id} onClick={() => navigate(`/assignments/${a.class_id}`)}
                className="w-full text-left p-4 border-b last:border-0 hover:bg-gray-50 transition-colors flex items-center justify-between"
                data-testid={`recent-assignment-${a.id}`}>
                <div>
                  <p className="font-medium text-[#002147]">{a.title}</p>
                  <p className="text-xs text-gray-500">{a.subject} &middot; {a.class_id?.toUpperCase().replace("-", " ")} &middot; Due: {new Date(a.due_date).toLocaleDateString()} &middot; {a.max_points} pts</p>
                </div>
                <ChevronRight size={16} className="text-gray-400" />
              </button>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
export const TeacherAttendancePage = () => <div className="py-16 bg-gradient-to-b from-yellow-400 to-orange-400 min-h-screen"><div className="max-w-7xl mx-auto px-4"><h1 className="text-3xl font-bold text-[#002147]">Teacher Attendance</h1></div></div>;
export const TeacherGradesPage = () => <div className="py-16 bg-gradient-to-b from-yellow-400 to-orange-400 min-h-screen"><div className="max-w-7xl mx-auto px-4"><h1 className="text-3xl font-bold text-[#002147]">Teacher Grades</h1></div></div>;

export const ResourceInfoPage = ({ title, description, content }) => (
  <div className="py-12 bg-gradient-to-b from-yellow-400 to-orange-400 min-h-screen">
    <div className="max-w-4xl mx-auto px-4">
      <Card className="bg-white">
        <CardHeader className="bg-[#002147] text-white rounded-t-lg">
          <CardTitle>{title}</CardTitle>
          <CardDescription className="text-gray-300">{description}</CardDescription>
        </CardHeader>
        <CardContent className="p-8">{content}</CardContent>
      </Card>
    </div>
  </div>
);
